import { createContext, useContext, Children } from "react";
import colors from "@/config/colors";
import { getEconomyDisplayName } from "@/utils/format";
import styles from "./styles.module.scss";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { positionY } from "../utils";
import type { Placement } from "../utils";

const LabelContext = createContext<{
  placement: Placement;
  numItems: number;
  width: number;
}>({
  placement: [],
  numItems: 0,
  width: 0,
});

interface RootProps {
  x: number;
  bottom: number;
  left: number;
  top?: number;
}

function Root({
  x,
  bottom,
  top,
  left,
  children,
}: React.PropsWithChildren<RootProps>) {
  const is1200px = useMediaQuery("(min-width: 1200px)");
  const padding = is1200px ? 15 : 8;
  const width = Math.min(window.innerWidth - left - 15, 250);

  return (
    <div
      className={styles["label-container"]}
      style={{
        left: x + padding,
        bottom,
        top,
      }}
    >
      <LabelContext.Provider
        value={{ placement: [], numItems: Children.count(children), width }}
      >
        <div className={styles["label-content"]}>{children}</div>
      </LabelContext.Provider>
    </div>
  );
}

interface ItemProps {
  colorName: keyof typeof colors;
  economyCode: string;
  y: number;
}

function Item({ colorName, economyCode, y }: ItemProps) {
  const { placement, numItems, width } = useContext(LabelContext);

  const is1024px = useMediaQuery("(min-width: 1024px)");
  const is600px = useMediaQuery("(min-width: 600px)");

  const height = is1024px ? 16 : is600px ? 14 : 10;

  const labelY = positionY({ y, height, economyCode, placement });

  if (placement.length >= numItems) {
    while (placement.length > 0) {
      placement.pop();
    }
  }

  return (
    <div
      style={{
        top: labelY,
        left: 0,
        width: width,
        height: height,
      }}
      className={styles.label}
    >
      <div
        className={styles["label-dot"]}
        style={{
          backgroundColor: colors[colorName],
        }}
      />
      <p className={styles["label-text"]} style={{ width }}>
        {getEconomyDisplayName(economyCode)}
      </p>
    </div>
  );
}

export { Root, Item };
